import InfoOutlined from "@mui/icons-material/InfoOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import { getDocumentNavigationUrl } from "@utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type {
  DataKeyResolution,
  DynamicNodeOption,
  DynamicNodeOwner,
} from "./dynamic-node-context";
import { DynamicNodePopover } from "./dynamic-node-popover";
import {
  DYNAMIC_NODE_EDIT_EVENT,
  hasNativeDynamicNodeTextSelection,
  isEventFromCurrentTarget,
} from "./dynamic-node-selection";
import "./dynamic-nodes.css";
import { renderMarkdownToHtml } from "./render-markdown";

const MAX_RESULTS = 50;

/** Visual/semantic state of the chip, drives its label + styling. */
type ChipState = "empty" | "loading" | "unresolved" | "resolved";

/**
 * Per-node-kind configuration for the shared chip view. The data-key and context
 * nodes differ only in their attribute, picker options, copy, and auto-open flag —
 * everything else (states, colors, popover, keyboard nav) is shared here.
 */
export interface DynamicChipConfig {
  /** Node attribute holding the selected id (e.g. `dataKey`, `contextKey`). */
  attr: string;
  /** Resolve the current id to a value + provenance. */
  resolution: DataKeyResolution | null;
  /** Explicit owner metadata for the selected value, when known. */
  owner?: DynamicNodeOwner;
  /** Picker options, read lazily so a mutable registry is re-read on open. */
  getItems: () => DynamicNodeOption[];
  /** Consume the one-shot "open picker on mount" signal for this node kind. */
  consumePendingOpen: () => boolean;
  /** True while the backing data is still loading (drives the loading state). */
  loading: boolean;
  /** True when previewing an approved/frozen version (styling only). */
  frozen: boolean;
  /**
   * Whether the chip responds to clicks (opens the picker). False while the
   * editor is read-only (previewing a locked/approved version) — the chip then
   * just renders its resolved value and ignores clicks/auto-open.
   */
  interactive: boolean;
  /** Uppercase header shown atop the picker bubble (e.g. "Data key"). */
  headerLabel: string;
  /** Placeholder shown when no id is selected yet. */
  emptyLabel: string;
  /** Search input placeholder. */
  searchPlaceholder: string;
  /** Message shown when the search yields nothing. */
  noMatchLabel: string;
  /** Render the resolved value as literal text instead of Markdown. */
  renderResolvedAsPlainText?: boolean;
  /** Show internal option/selection keys in the picker. */
  showKeys?: boolean;
  /** Show a compact link to the selected owner after the resolved value. */
  showInlineOwnerLink?: boolean;
  /** Treat a stored selection sentinel as empty while its picker is open. */
  isEmptyValue?: (value: string) => boolean;
  /**
   * Optional info panel toggled by an (i) button in the bubble header. Used by
   * data-key and document-reference nodes to explain their available values;
   * omitted by the context node, whose context map always returns in full.
   */
  info?: {
    /** Show the resolution colour legend (green / yellow / red swatches). */
    legend?: boolean;
    /** Explanatory content shown under the legend. */
    note?: React.ReactNode;
  };
}

interface DynamicChipNodeViewProps {
  node: NodeViewProps["node"];
  updateAttributes: NodeViewProps["updateAttributes"];
  deleteNode: NodeViewProps["deleteNode"];
  config: DynamicChipConfig;
  onSelect?: () => void;
}

/**
 * Inline "button" view shared by the data-key and context nodes. Renders the
 * live-resolved value highlighted (green resolved / yellow empty+loading / red
 * unresolved); clicking opens a comment-style bubble to pick/change the id and
 * shows the value's provenance. The node stays stateless — only the id is stored.
 */
export const DynamicChipNodeView: React.FC<DynamicChipNodeViewProps> = ({
  node,
  updateAttributes,
  deleteNode,
  config,
  onSelect,
}) => {
  const {
    attr,
    resolution,
    owner,
    getItems,
    consumePendingOpen,
    loading,
    frozen,
    interactive,
    headerLabel,
    emptyLabel,
    searchPlaceholder,
    noMatchLabel,
    renderResolvedAsPlainText = false,
    showKeys = true,
    showInlineOwnerLink = false,
    isEmptyValue,
    info,
  } = config;

  const selectedKey = (node.attrs[attr] as string) ?? "";
  const isEmpty = isEmptyValue?.(selectedKey) ?? selectedKey === "";
  const { deviceId = "", orgId = "" } = useParams();

  const [editing, setEditing] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const wrapperRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  /**
   * Focus the search field the moment it mounts — i.e. when the bubble opens,
   * including the auto-open-on-insert case. A *stable* callback ref fires only on
   * mount/unmount, so it focuses exactly once per open and doesn't steal focus
   * when Popper updates the bubble position.
   */
  const focusSearchOnMount = useCallback(
    (element: HTMLInputElement | null): void => {
      element?.focus();
    },
    []
  );

  // Open the picker on mount for a freshly inserted (empty) node. The one-shot
  // flag is consumed HERE, in an effect — not in the `useState` initializer —
  // because React StrictMode double-invokes initializers, which would consume the
  // flag on a throwaway render and leave the node closed. Effects also run twice
  // under StrictMode (mount → cleanup → mount), but component state survives that
  // simulated remount, so the first run opens the bubble and it stays open.
  useEffect(() => {
    if (interactive && isEmpty && consumePendingOpen()) {
      setEditing(true);
    }
    // Mount-only: intentionally not reactive to `selectedKey`/`consumePendingOpen`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element || !interactive) {
      return;
    }
    const openEditor = () => setEditing(true);
    element.addEventListener(DYNAMIC_NODE_EDIT_EVENT, openEditor);
    return () =>
      element.removeEventListener(DYNAMIC_NODE_EDIT_EVENT, openEditor);
  }, [interactive]);

  const items = useMemo(() => {
    const all = getItems();
    const normalized = query.trim().toLowerCase();
    const filtered = normalized
      ? all.filter(
          (item) =>
            item.key.toLowerCase().includes(normalized) ||
            item.label?.toLowerCase().includes(normalized) ||
            item.subtitle?.toLowerCase().includes(normalized)
        )
      : all;
    return filtered.slice(0, MAX_RESULTS);
    // `editing` re-reads the (possibly mutable) option source when reopened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, editing]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Close the bubble on an outside click.
  useEffect(() => {
    if (!editing) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (
        popoverRef.current?.contains(target) ||
        wrapperRef.current?.contains(target)
      ) {
        return;
      }
      setEditing(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [editing]);

  const selectKey = (key: string): void => {
    updateAttributes({ [attr]: key });
    setQuery("");
    setEditing(false);
  };
  const handleChipClick = (event: React.MouseEvent): void => {
    if (!isEventFromCurrentTarget(event.currentTarget, event.target)) {
      return;
    }
    if (
      hasNativeDynamicNodeTextSelection(
        event.currentTarget,
        event.currentTarget.ownerDocument.getSelection()
      )
    ) {
      return;
    }
    if (onSelect) {
      onSelect();
    } else {
      setEditing(true);
    }
  };

  const onInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const nextIndex = items.length ? (activeIndex + 1) % items.length : 0;
      setActiveIndex(nextIndex);
      itemRefs.current[nextIndex]?.scrollIntoView({ block: "nearest" });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const nextIndex = items.length
        ? (activeIndex + items.length - 1) % items.length
        : 0;
      setActiveIndex(nextIndex);
      itemRefs.current[nextIndex]?.scrollIntoView({ block: "nearest" });
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = items[activeIndex];
      if (item) {
        selectKey(item.key);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setEditing(false);
    }
  };

  const state: ChipState = isEmpty
    ? "empty"
    : resolution && !resolution.isUnresolved
      ? "resolved"
      : loading
        ? "loading"
        : "unresolved";

  const label =
    state === "empty"
      ? emptyLabel
      : state === "resolved"
        ? (resolution?.value ?? "")
        : state === "loading"
          ? "Loading…"
          : selectedKey;

  // Data keys are authored in a markdown editor, so a resolved value is always
  // markdown: render it through the editor's own schema (matching the document)
  // rather than a foreign preview engine. Block values (tables/images/lists) sit on
  // their own line; inline values flow within the surrounding sentence.
  const rendered = useMemo(
    () =>
      state === "resolved" && !renderResolvedAsPlainText
        ? renderMarkdownToHtml(label)
        : null,
    [state, label, renderResolvedAsPlainText]
  );
  const chipClassName = [
    "tiptap-data-key",
    `tiptap-data-key--${state}`,
    rendered?.isBlock ? "tiptap-data-key--rich" : "",
    frozen ? "tiptap-data-key--frozen" : "",
    editing ? "tiptap-data-key--editing" : "",
    interactive ? "" : "tiptap-data-key--readonly",
  ]
    .filter(Boolean)
    .join(" ");

  const renderResolutionStatus = (): React.ReactNode => {
    if (resolution && !resolution.isUnresolved) {
      if (resolution.sourceDocumentTitle) {
        return (
          <span>
            Source: <strong>{resolution.sourceDocumentTitle}</strong>
          </span>
        );
      }
      return <span>Resolved from document context</span>;
    }
    if (loading) {
      return <span>Loading current value…</span>;
    }
    return <span>No value found for this key yet</span>;
  };

  const selectedItem = getItems().find((item) => item.key === selectedKey);
  const selectedOwner: DynamicNodeOwner | undefined =
    owner ??
    (resolution?.sourceDocumentId
      ? {
          title:
            resolution.sourceDocumentTitle ??
            selectedItem?.owner?.title ??
            selectedKey,
          documentId: resolution.sourceDocumentId,
          deviceId: resolution.sourceDocumentDeviceId,
        }
      : selectedItem?.owner);
  const ownerHref =
    orgId && deviceId && selectedOwner?.documentId
      ? getDocumentNavigationUrl(
          orgId,
          selectedOwner.deviceId ?? deviceId,
          selectedOwner.documentId,
          selectedOwner.versionId
        )
      : null;

  return (
    <NodeViewWrapper
      as="span"
      ref={wrapperRef}
      className={chipClassName}
      contentEditable={false}
      data-dynamic-node-editor
      data-data-key={selectedKey}
      title={isEmpty ? emptyLabel : selectedKey}
      onClick={interactive ? handleChipClick : undefined}
    >
      {rendered ? (
        rendered.isBlock ? (
          <div
            className="tiptap-data-key__rich-value"
            data-color-mode="light"
            dangerouslySetInnerHTML={{ __html: rendered.html }}
          />
        ) : (
          <span
            className="tiptap-data-key__value"
            dangerouslySetInnerHTML={{ __html: rendered.html }}
          />
        )
      ) : (
        <span className="tiptap-data-key__value">{label}</span>
      )}
      {showInlineOwnerLink &&
      state === "resolved" &&
      selectedOwner &&
      ownerHref ? (
        <Link
          to={ownerHref}
          target="_blank"
          rel="noopener noreferrer"
          className="tiptap-data-key__document-link"
          aria-label={`Open ${selectedOwner.title} in a new tab`}
          title={`Open ${selectedOwner.title} in a new tab`}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <OpenInNew aria-hidden="true" fontSize="inherit" />
        </Link>
      ) : null}

      {interactive && editing && wrapperRef.current ? (
        <DynamicNodePopover
          ref={popoverRef}
          anchorEl={wrapperRef.current}
          className="tiptap-data-key-popover"
        >
          <div className="tiptap-data-key-popover__header">
            <span>{headerLabel}</span>
            {info ? (
              <button
                type="button"
                className={
                  showInfo
                    ? "tiptap-data-key-popover__info is-active"
                    : "tiptap-data-key-popover__info"
                }
                title="Show resolution status"
                aria-pressed={showInfo}
                onMouseDown={(event) => {
                  event.preventDefault();
                  setShowInfo((value) => !value);
                }}
              >
                <InfoOutlined fontSize="inherit" />
              </button>
            ) : null}
          </div>
          {info && showInfo ? (
            <div className="tiptap-data-key-popover__legend">
              {info.legend ? (
                <>
                  <span className="tiptap-data-key-popover__legend-item">
                    <span className="tiptap-data-key-popover__swatch tiptap-data-key-popover__swatch--resolved" />
                    value resolved
                  </span>
                  <span className="tiptap-data-key-popover__legend-item">
                    <span className="tiptap-data-key-popover__swatch tiptap-data-key-popover__swatch--loading" />
                    value loading
                  </span>
                  <span className="tiptap-data-key-popover__legend-item">
                    <span className="tiptap-data-key-popover__swatch tiptap-data-key-popover__swatch--unresolved" />
                    value not found
                  </span>
                </>
              ) : null}
              {info.note ? (
                <div className="tiptap-data-key-popover__legend-note">
                  {info.note}
                </div>
              ) : null}
            </div>
          ) : null}
          <input
            ref={focusSearchOnMount}
            className="tiptap-data-key-popover__search"
            value={query}
            placeholder={searchPlaceholder}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
          />
          <div className="tiptap-data-key-popover__list" role="listbox">
            {items.length === 0 ? (
              <div className="tiptap-data-key-popover__empty">
                {noMatchLabel}
              </div>
            ) : (
              items.map((item, index) => {
                const subtitle =
                  item.subtitle ?? (item.label && showKeys ? item.key : null);

                return (
                  <button
                    key={item.key}
                    ref={(element) => {
                      itemRefs.current[index] = element;
                    }}
                    type="button"
                    role="option"
                    aria-selected={item.key === selectedKey}
                    className={[
                      "tiptap-data-key-popover__item",
                      index === activeIndex ? "is-active" : "",
                      item.key === selectedKey ? "is-selected" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectKey(item.key);
                    }}
                  >
                    <span className="tiptap-data-key-popover__item-label">
                      {item.label ?? item.key}
                    </span>
                    {subtitle ? (
                      <span
                        className="tiptap-data-key-popover__item-key"
                        title={item.subtitle}
                      >
                        {subtitle}
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
          {!isEmpty ? (
            <div className="tiptap-data-key-popover__footer">
              <div className="tiptap-data-key-popover__selection">
                {showKeys || (selectedOwner && !showInlineOwnerLink) ? (
                  <div className="tiptap-data-key-popover__selection-row">
                    {showKeys ? (
                      <span
                        className="tiptap-data-key-popover__selected-key"
                        aria-label="Selected data key"
                      >
                        Selected: {selectedKey}
                      </span>
                    ) : null}
                    {selectedOwner && !showInlineOwnerLink ? (
                      ownerHref ? (
                        <Link
                          to={ownerHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tiptap-data-key-popover__owner-link"
                          aria-label={`Open data key owner ${selectedOwner.title} in a new tab`}
                          title={`Open ${selectedOwner.title} in a new tab`}
                        >
                          <OpenInNew aria-hidden="true" fontSize="small" />
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="tiptap-data-key-popover__owner-link"
                          aria-label={`Data key owner ${selectedOwner.title} has no document yet`}
                          title={`${selectedOwner.title} has not been created yet`}
                          disabled
                        >
                          <OpenInNew aria-hidden="true" fontSize="small" />
                        </button>
                      )
                    ) : null}
                  </div>
                ) : null}
                <span className="tiptap-data-key-popover__status">
                  {renderResolutionStatus()}
                </span>
              </div>
              <button
                type="button"
                className="tiptap-data-key-popover__remove"
                onMouseDown={(event) => {
                  event.preventDefault();
                  deleteNode();
                }}
              >
                Remove
              </button>
            </div>
          ) : null}
        </DynamicNodePopover>
      ) : null}
    </NodeViewWrapper>
  );
};

export default DynamicChipNodeView;
